'use client';

import { Box, Button, Stack, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { useUser } from '~/hooks/useUser';
import { useToast } from '~/lib/toasts';
import { postAPI } from '~/lib/api';
import { API_PATHS } from '@habity/constants';

import { FloatingLabelInput } from '~/components/ui/forms/FloatingLabelInput';
import {
  GoogleAddressAutocomplete,
  type GoogleAddressResult,
} from '~/components/ui/forms/GoogleAddressAutocomplete';

interface LeadCaptureFormProps {
  value: {
    form: {
      formType: 'seller' | 'buyer';
      showTopicField?: boolean;
      topic?: string;
      leadSourceId?: string;
      assignmentSourceId?: string;
      showAddressFields?: boolean;
      submitButtonText?: string;
      // Optional broker ID to assign leads directly (skips address matching)
      brokerId?: number;
      // Name of the form from Sanity (human-readable, for logging)
      formName?: string;
    };
  };
}

export const LeadCaptureForm = ({ value }: LeadCaptureFormProps) => {
  if (!value?.form) {
    return null;
  }

  // Require leadSourceId to be configured in Sanity
  if (!value.form.leadSourceId) {
    console.error(
      'LeadCaptureForm: leadSourceId is required but not configured in Sanity'
    );
    return null;
  }

  // For AcceptObjectFlow lead sources, assignmentSourceId is required
  // Note: We can't check the lead source type here, so we rely on the Sanity UI to enforce this
  // The AssignmentSourceInput component will only show for AcceptObjectFlow lead sources

  const { user } = useUser();
  const { showToast } = useToast();
  const {
    formType,
    showTopicField = false,
    topic: formTopic,
    leadSourceId,
    assignmentSourceId,
    showAddressFields = true,
    submitButtonText,
    brokerId,
    formName,
  } = value.form;

  const pathname = usePathname();

  // formPath is the page URL where the form was submitted
  const formPath = pathname || undefined;

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAddress, setSelectedAddress] =
    useState<GoogleAddressResult | null>(null);

  const defaultName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') || '';
  const defaultEmail = user?.email || '';
  const defaultPhone = (() => {
    const raw = user?.phone;
    if (!raw) return '';
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.value) return parsed.value;
    } catch {
      // Not JSON, use as-is
    }
    return raw;
  })();

  const validateName = (name: string): boolean => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setErrors((prev) => ({ ...prev, name: 'Namn är obligatoriskt' }));
      return false;
    }

    const parts = trimmedName.split(/\s+/);
    if (parts.length < 2) {
      setErrors((prev) => ({
        ...prev,
        name: 'Ange både förnamn och efternamn',
      }));
      return false;
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.name;
      return newErrors;
    });
    return true;
  };

  const validateEmail = (email: string): boolean => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrors((prev) => ({ ...prev, email: 'Email är obligatoriskt' }));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setErrors((prev) => ({ ...prev, email: 'Ange en giltig email-adress' }));
      return false;
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.email;
      return newErrors;
    });
    return true;
  };

  const validatePhone = (phone: string): boolean => {
    const trimmedPhone = phone.trim();
    if (!trimmedPhone) {
      setErrors((prev) => ({
        ...prev,
        phone: 'Telefonnummer är obligatoriskt',
      }));
      return false;
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.phone;
      return newErrors;
    });
    return true;
  };

  const handleAddressChange = (result: GoogleAddressResult | null) => {
    setSelectedAddress(result);
    if (result) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.address;
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    const form = e.currentTarget;
    const formData = new FormData(form);
    const name = formData.get('name')?.toString() || '';
    const email = formData.get('email')?.toString() || '';
    const phone = formData.get('phone')?.toString() || '';
    const topic = formTopic || ''; // Use topic from Sanity form config

    // Build address from Google autocomplete selection
    const street = selectedAddress
      ? selectedAddress.streetNumber
        ? `${selectedAddress.street} ${selectedAddress.streetNumber}`
        : selectedAddress.street
      : '';
    const city = selectedAddress?.city || '';
    const zip = selectedAddress?.postalCode || '';

    // Validate all fields
    const isNameValid = validateName(name);
    const isEmailValid = validateEmail(email);
    const isPhoneValid = validatePhone(phone);
    const isAddressValid = showAddressFields
      ? (() => {
          if (!selectedAddress) {
            setErrors((prev) => ({ ...prev, address: 'Välj en adress' }));
            return false;
          }
          return true;
        })()
      : true;

    if (!isNameValid || !isEmailValid || !isPhoneValid || !isAddressValid) {
      setIsSubmitting(false);
      return;
    }

    // Split name into first and last name
    const nameParts = name.trim().split(/\s+/);
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');

    try {
      const action =
        formType === 'seller'
          ? 'entered_seller_lead_form'
          : 'entered_buyer_lead_form';

      const data = await postAPI(
        API_PATHS.PROVIDERS.SUPABASE.USERS.ACTION,
        JSON.stringify({
          email,
          action,
          userData: {
            first_name: firstName,
            last_name: lastName,
            name: name.trim(),
            phone,
            address_street: street,
            address_city: city,
            address_zip: zip,
            address_country: 'SE',
          },
          leadSourceId,
          assignmentSourceId,
          topic,
          leadFormBrokerId: brokerId,
          formName: formName || undefined,
          formPath,
        })
      );

      if (data?.error || !data?.user) {
        showToast(
          {
            title: 'Ett fel uppstod',
            description: data?.message || 'Försök igen senare',
          },
          { status: 'error' }
        );
        setIsSubmitting(false);
        return;
      }

      showToast(
        {
          title: 'Tack för din förfrågan!',
          description: 'Vi kommer att kontakta dig inom kort.',
        },
        { status: 'success' }
      );
      form.reset();
    } catch (error) {
      console.error('Error submitting form:', error);
      showToast(
        {
          title: 'Ett fel uppstod',
          description: 'Försök igen senare',
        },
        { status: 'error' }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      mt={12}
      mb={12}
      p={{ base: 6, md: 8 }}
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="md"
      maxW="480px"
      mx="auto"
    >
      <Stack spacing={4} align="stretch">
        <FloatingLabelInput
          label="Namn"
          name="name"
          type="text"
          placeholder="Förnamn Efternamn"
          defaultValue={defaultName}
          isInvalid={!!errors.name}
          error={errors.name}
          isRequired
          useFloatingLabel
        />

        <FloatingLabelInput
          label="E-postadress"
          name="email"
          type="email"
          placeholder="E-postadress"
          defaultValue={defaultEmail}
          isInvalid={!!errors.email}
          error={errors.email}
          isRequired
          useFloatingLabel
        />

        <FloatingLabelInput
          label="Telefon"
          name="phone"
          type="tel"
          placeholder="070-123 45 67"
          defaultValue={defaultPhone}
          isInvalid={!!errors.phone}
          error={errors.phone}
          isRequired
          useFloatingLabel
        />

        {showAddressFields && (
          <Box>
            <GoogleAddressAutocomplete
              onChange={handleAddressChange}
              placeholder="Sök adress..."
            />
            {errors.address && (
              <Text color="red.500" fontSize="sm" mt={1}>
                {errors.address}
              </Text>
            )}
          </Box>
        )}

        {leadSourceId && (
          <input type="hidden" name="leadSourceId" value={leadSourceId} />
        )}

        {assignmentSourceId && (
          <input
            type="hidden"
            name="assignmentSourceId"
            value={assignmentSourceId}
          />
        )}

        {formTopic && <input type="hidden" name="topic" value={formTopic} />}

        <Button
          type="submit"
          w="full"
          isLoading={isSubmitting}
          loadingText="Skickar..."
          mt={2}
          bg="blackAlpha.900"
          color="white"
          _hover={{ bg: 'blackAlpha.700' }}
        >
          {submitButtonText || 'Skicka'}
        </Button>
      </Stack>
    </Box>
  );
};
